import React, { useState } from 'react';
import { Check, Clock, BookOpen, ArrowRight, Play, X, Monitor, Users, MessageSquare, Award, Sparkles, ShoppingCart, Gift } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const BonusCourseModal = ({ isOpen, onClose, course, onEnrollSeparately }) => {
  const [activeImage, setActiveImage] = useState(0);

  if (!isOpen || !course) return null;

  const regularPrice = 297; // Preço regular fora das formações

  const extendedSyllabus = [
    { module: 'Módulo 1 - Introdução', topics: ['O que é o Design Review', 'Instalação e configuração', 'Interface do usuário', 'Navegação básica'] },
    { module: 'Módulo 2 - Importação de Projetos', topics: ['Formatos suportados (DWF, DWFx, PDF)', 'Importando arquivos do Revit', 'Importando arquivos do AutoCAD', 'Gerenciamento de sheets'] },
    { module: 'Módulo 3 - Ferramentas de Revisão', topics: ['Markup e anotações', 'Ferramentas de desenho', 'Caixas de texto e callouts', 'Carimbos personalizados'] },
    { module: 'Módulo 4 - Medições e Análises', topics: ['Ferramentas de medição', 'Cálculo de áreas', 'Comparação de versões', 'Detecção de alterações'] },
    { module: 'Módulo 5 - Colaboração', topics: ['Fluxo de revisão', 'Exportação de markups', 'Integração com equipe', 'Relatórios de revisão'] }
  ];

  const softwareImages = [
    'https://images.unsplash.com/photo-1545670723-196ed0954986?w=800&q=80',
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
    'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&q=80'
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-zinc-900 rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto border border-green-500/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header com imagem */}
        <div className="relative h-56 bg-gradient-to-br from-green-600 to-emerald-800 overflow-hidden">
          <img
            src={softwareImages[activeImage]}
            alt="Design Review"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
            <Badge className="bg-green-500 text-white mb-3 text-lg px-4 py-2">
              <Gift className="mr-2" size={18} />
              CURSO BÔNUS
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">Autodesk | Design Review</h2>
            <p className="text-green-100 text-lg">Revisão e colaboração de projetos BIM</p>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          >
            <X size={24} />
          </button>

          {/* Thumbnails */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {softwareImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveImage(idx)}
                className={`w-3 h-3 rounded-full transition-all ${activeImage === idx ? 'bg-white w-6' : 'bg-white/50'}`}
              />
            ))}
          </div>
        </div>

        <div className="p-6 md:p-8">
          {/* Badges de destaque */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Badge className="bg-green-600/20 text-green-400 border-green-500/30">
              <Sparkles size={14} className="mr-1" /> Gratuito nas Formações
            </Badge>
            <Badge className="bg-blue-600/20 text-blue-400 border-blue-500/30">
              <Clock size={14} className="mr-1" /> 2 horas
            </Badge>
            <Badge className="bg-purple-600/20 text-purple-400 border-purple-500/30">
              <Monitor size={14} className="mr-1" /> Software Autodesk
            </Badge>
            <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-500/30">
              <Award size={14} className="mr-1" /> Certificado incluso
            </Badge>
          </div>

          {/* Descrição */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-white mb-3">Sobre o Curso</h3>
            <p className="text-gray-300 leading-relaxed">
              O <strong className="text-green-400">Autodesk Design Review</strong> é uma ferramenta essencial para revisão e colaboração em projetos BIM.
              Com ele, você pode visualizar, marcar e compartilhar comentários em arquivos DWF, DWFx e PDF sem precisar
              do software original de criação. Ideal para coordenadores, clientes e equipes de revisão que precisam
              analisar projetos de forma eficiente e organizada.
            </p>
          </div>

          {/* Grid de features */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-zinc-800 rounded-xl p-4 text-center">
              <Users className="text-green-500 mx-auto mb-2" size={28} />
              <div className="text-white font-semibold">Colaboração</div>
              <div className="text-gray-400 text-sm">Revisão em equipe</div>
            </div>
            <div className="bg-zinc-800 rounded-xl p-4 text-center">
              <MessageSquare className="text-green-500 mx-auto mb-2" size={28} />
              <div className="text-white font-semibold">Markup</div>
              <div className="text-gray-400 text-sm">Anotações e comentários</div>
            </div>
            <div className="bg-zinc-800 rounded-xl p-4 text-center">
              <BookOpen className="text-green-500 mx-auto mb-2" size={28} />
              <div className="text-white font-semibold">5 Módulos</div>
              <div className="text-gray-400 text-sm">Conteúdo completo</div>
            </div>
          </div>

          {/* Programa Didático */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center">
              <BookOpen className="mr-2 text-green-500" size={20} />
              Programa Didático
            </h3>
            <div className="space-y-3">
              {extendedSyllabus.map((module, idx) => (
                <div key={`bonus-mod-${idx}`} className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-7 h-7 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {idx + 1}
                    </span>
                    <h4 className="text-white font-semibold">{module.module}</h4>
                  </div>
                  <div className="grid md:grid-cols-2 gap-1 pl-9">
                    {module.topics.map((topic) => (
                      <div key={topic} className="flex items-center gap-2 text-sm text-gray-300">
                        <Check size={12} className="text-green-400 flex-shrink-0" />
                        <span>{topic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preço e CTA */}
          <div className="bg-gradient-to-r from-zinc-800 to-zinc-800/50 rounded-xl p-6 border border-zinc-700">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Opção 1: Gratuito nas formações */}
              <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-5 text-center">
                <Gift className="text-green-400 mx-auto mb-2" size={32} />
                <div className="text-green-400 font-bold text-lg mb-1">GRÁTIS</div>
                <div className="text-white text-2xl font-bold mb-2">R$ 0,00</div>
                <div className="text-gray-400 text-sm mb-4">
                  Incluído em qualquer formação
                </div>
                <Button
                  onClick={() => {
                    onClose();
                    document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  Ver Formações
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>

              {/* Opção 2: Comprar separado */}
              <div className="bg-zinc-800 border border-zinc-600 rounded-xl p-5 text-center">
                <ShoppingCart className="text-gray-400 mx-auto mb-2" size={32} />
                <div className="text-gray-400 font-bold text-lg mb-1">AVULSO</div>
                <div className="text-white text-2xl font-bold mb-2">R$ {regularPrice.toFixed(2).replace('.', ',')}</div>
                <div className="text-gray-400 text-sm mb-4">
                  Comprar curso individual
                </div>
                <Button
                  onClick={() => onEnrollSeparately(3)}
                  variant="outline"
                  className="w-full border-gray-600 text-gray-300 hover:bg-zinc-700"
                >
                  Comprar Avulso
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="text-green-400 text-sm font-medium">
                💡 Dica: Ao matricular-se em qualquer formação, você ganha este curso de bônus!
              </p>
            </div>
          </div>

          {/* O que está incluído */}
          <div className="mt-6 grid md:grid-cols-4 gap-3">
            {[
              { icon: Play, text: 'Acesso vitalício' },
              { icon: Award, text: 'Certificado' },
              { icon: MessageSquare, text: 'Suporte' },
              { icon: BookOpen, text: 'Material didático' }
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2 text-sm text-gray-400">
                <item.icon size={16} className="text-green-500" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BonusCourseModal;
